import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { COLORS } from './game-engine/config';
import { useGameController } from './hooks';
import { PlayButton, RecentBins } from './components';
import { PlaceYourBet } from './components/PlaceYourBet';
import { InnerShape } from './components/Shape';
import imgHoney from './assets/honey.png'
import { useAtomValue, useSetAtom } from 'jotai';
import {
  atomUiHidden,
  atomBoardReady,
  atomControlsReady,
  atomIsReplayMode
} from './states/game.atoms';
import { BackgroundLayer } from './components/BackgroundLayer';
import { BoardLayer } from './components/BoardLayer';
import { useAssets } from './components/AssetHandler';
import { GameActionsProvider } from './contexts/GameActionsContext';
import { useResolution } from './hooks/useResolution';
import svgLogo from './assets/logo.png'

interface BlinkoReplayProps {
  replayGameId: string;
}

const BlinkoReplay: React.FC<BlinkoReplayProps> = ({ replayGameId }) => {
  const { isMobile } = useResolution()

  const { canvasRef: controllerCanvasRef, canvasDimensions, actions, replayGame } = useGameController();

  // Atoms for UI
  const uiHidden = useAtomValue(atomUiHidden);
  
  // Loading Coordination
  const { markAssetsReady } = useAssets();
  const boardReady = useAtomValue(atomBoardReady);
  const controlsReady = useAtomValue(atomControlsReady);

  useEffect(() => {
    if (boardReady && controlsReady) {
      markAssetsReady();
    }
  }, [boardReady, controlsReady, markAssetsReady]);

  const setIsReplayMode = useSetAtom(atomIsReplayMode);

  const loadedGameIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    setIsReplayMode(true);
    if (!replayGameId || !boardReady || !controlsReady) return;
    
    // Prevent infinite loop if already loaded
    if (loadedGameIdRef.current === replayGameId) return;

    loadedGameIdRef.current = replayGameId;
    replayGame(replayGameId);
  }, [replayGameId, boardReady, controlsReady, replayGame, setIsReplayMode]);

  return (
    <GameActionsProvider actions={actions}>

      <div className="fixed inset-0 z-50 pointer-events-none">
        <div 
          className="absolute inset-0 p-[2px] md:p-[3px] bg-gradient-to-r from-amber-200 via-orange-300 to-amber-500"
          style={{ 
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', 
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', 
            maskComposite: 'exclude', 
            WebkitMaskComposite: 'xor' 
          }} 
        />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-200 via-orange-300 to-amber-500 text-amber-950 px-6 py-0 rounded-b-lg font-bold text-xs md:text-sm font-blinko">
          Replay Mode
        </div>
      </div>      <div style={{
        backgroundColor: COLORS.background,
        minHeight: '100dvh',
      }}>
      <div className='fixed inset-0 overflow-hidden'>
        <BackgroundLayer />
        
        <motion.div
          animate={{ opacity: uiHidden ? 0 : 1 }}
          transition={{ duration: 0.5 }}
        >
          <BoardLayer /> 

          <canvas
            ref={controllerCanvasRef}
            width={canvasDimensions.width}
            height={canvasDimensions.height}
            className="absolute inset-0 z-20"
          />
        </motion.div>
        
        
      </div>

      <div className='absolute inset-0 overflow-hidden'>
        <motion.div
          initial={false}
          animate={{ opacity: uiHidden ? 0 : 1 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          style={{ pointerEvents: uiHidden ? 'none' : 'auto' }}
        >
          <div className='absolute left-0 bottom-0 pl-4 pb-[140px] md:pl-8 md:pb-10'>
            <img src={imgHoney} className='absolute right-4 bottom-2 w-[100px] hidden md:block'/>
            <div className='relative'>
              <RecentBins />
            </div>
          </div>

          <div className='pointer-events-none absolute left-0 md:left-auto right-0 bottom-0 px-4 py-4 md:px-8 md:py-10'>
            <div className='flex items-center justify-end -mb-5 -mr-5 md:m-0 md:justify-center'> 
              <div className='pointer-events-auto'>
                <PlayButton />
              </div>
            </div>
            
            <div className='pointer-events-auto'>
              <PlaceYourBet />
            </div>
          </div>

          {isMobile ? (
            <div className='absolute left-0 top-0 right-0 px-2.5 py-2 flex items-center justify-between'>
              <img src={svgLogo} alt="logo" className="w-[157px]" />

              <a href="/" target='_blank'>
                <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                  <div className="px-3 py-1.5">
                    <p className="font-blinko text-xs text-white">Play Now</p>
                  </div>
                </InnerShape>
              </a>
            </div>
          ) : (
            <div className="absolute left-0 top-0 right-0 px-8 py-6 flex items-center justify-between">
              <img src={svgLogo} alt="logo" className="w-[200px]" />

              <a href="/" target='_blank'>
                <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                  <div className="flex justify-center items-center gap-3 px-8 py-3">
                    <p className="text-base font-blinko text-white">Play Now</p>
                  </div>
                </InnerShape>
              </a>
            </div>
          )}
        </motion.div>
      </div>
      </div>
    </GameActionsProvider>
  );
};

export default BlinkoReplay;
